
import { CommonSortInput, GQL_ID, NestedFilterInput, PageInfo, Product } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'


import { ProductWithOfferFragment } from '@entities/Product/fragments'

import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

export type ShopProductsQuery = {
  products: {
    edges: Product[]
    pageInfo: ShopProductsPageInfo
  }
}

type ShopProductsPageInfo = PageInfo & { total?: number | null }

export type ShopProductsVariables = {
  shopId: GQL_ID
  categoryId?: GQL_ID
  page?: number
  limit?: number
  style?: string
  sort?: CommonSortInput
  filter?: NestedFilterInput
}

export const GQL_QUERY_SHOP_PRODUCTS = gql`
  query products (
    $shopId: ID!
    $categoryId: ID
    $page: Int
    $limit: Int
    $style: String
    $sort: CommonSortInput
    $filter: NestedFilterInput
  ) {
    products(
      shop_id: $shopId
      category_id: $categoryId
      sort: $sort
      filter: $filter
      _page: $page
      _limit: $limit
    ) {
      edges {
        ...ProductWithOffer
        categories {
          id
        }
        short_description
        
      }
      pageInfo {
        page
        lastPage
        perPage
        total
      }
    }
  }
  ${ProductWithOfferFragment}
`
export const useFetchShopProducts = (variables: ShopProductsVariables) => {
  const { data, loading, error, refetch, fetchMore, ...rest } = useQuery<ShopProductsQuery, ShopProductsVariables>(
    GQL_QUERY_SHOP_PRODUCTS,
    {
      variables,
      skip: !variables.shopId,
      fetchPolicy: 'cache-and-network',
    },
  )

  const products = useMemo(() => data?.products?.edges ?? [], [ data?.products?.edges ])
  const pageInfo = useMemo(() => data?.products?.pageInfo, [ data?.products?.pageInfo ])

  const { hasMore, loadingMore, fetchNextPage, clearPagination } =
    useFetchMorePagination<ShopProductsQuery, ShopProductsVariables>({
      loading,
      fetchMore,
      page: variables.page ?? 1,
      pageVariable: 'page',
      variables,
      updaterFunction: (cached, fetched) => {
        const cachedProducts = cached?.products ?? { edges: [], pageInfo: undefined }
        const fetchedProducts = fetched?.products ?? { edges: [], pageInfo: undefined }
        const prev = cachedProducts.edges ?? []
        const curr = fetchedProducts.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            products: {
              ...cachedProducts,
              edges: [ ...prev, ...curr ],
              pageInfo: fetchedProducts.pageInfo ?? cachedProducts.pageInfo,
            },
          },
        }
      },
    })

  return {
    data,
    products,
    pageInfo,
    loading,
    loadingMore,
    hasMore,
    fetchNextPage,
    clearPagination,
    error,
    refetch,
    fetchMore,
    ...rest,
  }
}
